import { useState, useEffect } from 'react';
import axios from 'axios';
import SalaryBreakdownModal from '../../components/SalaryBreakdownModal';
import SalaryFundHistoryModal from '../../components/SalaryFundHistoryModal';
import DashboardLayout from '../../components/DashboardLayout';
import Swal from 'sweetalert2';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : 'http://localhost:3000/api';

const EXPORT_PERIOD_LABELS = {
  monthly: 'Bulanan',
  quarter: '3 Bulan',
  semester: '6 Bulan',
  yearly: 'Tahunan'
};

const getCurrentMonthValue = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${now.getFullYear()}-${month}`;
};

const toISODate = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getExportDateRange = (period, yearMonth) => {
  const [year, month] = yearMonth.split('-').map(Number);

  if (period === 'yearly') {
    return {
      start_date: `${year}-01-01`,
      end_date: `${year}-12-31`
    };
  }

  const endDate = new Date(year, month, 0);
  const monthsBack = period === 'monthly' ? 1 : period === 'quarter' ? 3 : 6;
  const startDate = new Date(year, month - monthsBack, 1);

  return {
    start_date: toISODate(startDate),
    end_date: toISODate(endDate)
  };
};

const csvEscape = (value) => {
  const str = String(value ?? '');
  if (/[",\n]/.test(str)) {
    return `"${str.replace(/"/g, '""')}"`;
  }
  return str;
};

const downloadCsv = (content, filename) => {
  const blob = new Blob(['\uFEFF', content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const Finance = () => {
  const [summary, setSummary] = useState({
    total: {
      total_income: 0,
      total_expense: 0,
      total_balance: 0,
      total_salary_expense: 0
    },
    channels: []
  });
  const [transactions, setTransactions] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showBreakdownModal, setShowBreakdownModal] = useState(false);
  const [filterChannel, setFilterChannel] = useState('');
  const [filterType, setFilterType] = useState('');
  const [showProporsiModal, setShowProporsiModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [proporsiAmount, setProporsiAmount] = useState('');
  const [proporsiChannel, setProporsiChannel] = useState('');
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportPeriod, setExportPeriod] = useState('monthly');
  const [exportMonth, setExportMonth] = useState(getCurrentMonthValue());
  const [exportChannel, setExportChannel] = useState('');
  const [exportType, setExportType] = useState('');
  const [exportLoading, setExportLoading] = useState(false);

  const [formData, setFormData] = useState({
    channel_id: '',
    type: 'income',
    category: '',
    amount: '',
    description: '',
    transaction_date: new Date().toISOString().split('T')[0]
  });

  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchData();
  }, [filterChannel, filterType]);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [summaryRes, channelsRes, transactionsRes] = await Promise.all([
        axios.get(`${API_URL}/finance/summary`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/finance/channels`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_URL}/finance/transactions`, {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            channel_id: filterChannel || undefined,
            type: filterType || undefined,
            limit: 100
          }
        })
      ]);

      setSummary(summaryRes.data);
      setChannels(channelsRes.data);
      setTransactions(transactionsRes.data);
    } catch (error) {
      console.error('Error fetching finance data:', error);
      Swal.fire('Error', 'Gagal memuat data keuangan', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.channel_id || !formData.category || !formData.amount) {
      Swal.fire('Peringatan', 'Mohon lengkapi semua field yang required', 'warning');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/finance/transactions`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      Swal.fire('Berhasil', 'Transaksi berhasil ditambahkan', 'success');
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating transaction:', error);
      Swal.fire('Error', 'Gagal menambahkan transaksi', 'error');
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Yakin hapus transaksi?',
      text: "Data yang dihapus tidak bisa dikembalikan!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Ya, hapus!'
    });
    if (!result.isConfirmed) return;

    try {
      await axios.delete(`${API_URL}/finance/transactions/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      Swal.fire('Berhasil', 'Transaksi berhasil dihapus', 'success');
      fetchData();
    } catch (error) {
      console.error('Error deleting transaction:', error);
      Swal.fire('Error', error.response?.data?.error || 'Gagal menghapus transaksi', 'error');
    }
  };

  const resetForm = () => {
    setFormData({
      channel_id: '',
      type: 'income',
      category: '',
      amount: '',
      description: '',
      transaction_date: new Date().toISOString().split('T')[0]
    });
  };

  const handleProporsiSubmit = async (e) => {
    e.preventDefault();
    const parsedAmount = parseFloat(proporsiAmount.replace(/[^0-9]/g, ''));
    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      Swal.fire('Error', 'Nominal tidak valid', 'error');
      return;
    }
    if (!proporsiChannel) {
      Swal.fire('Error', 'Pilih sumber channel', 'error');
      return;
    }
    try {
      await axios.post(`${API_URL}/finance/salary-funds`, {
        amount: parsedAmount,
        description: 'Penambahan proporsi dana gaji',
        channel_id: proporsiChannel
      }, { headers: { Authorization: `Bearer ${token}` } });
      Swal.fire('Berhasil', 'Proporsi gaji berhasil ditambahkan', 'success');
      setShowProporsiModal(false);
      setProporsiAmount('');
      setProporsiChannel('');
      fetchData();
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Gagal menambahkan proporsi gaji', 'error');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatInputCurrency = (value) => {
    const numericValue = value.toString().replace(/[^0-9]/g, '');
    if (!numericValue) return '';
    return parseInt(numericValue, 10).toLocaleString('id-ID');
  };

  const unformatCurrency = (value) => {
    return value.toString().replace(/[^0-9]/g, '');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  const buildFinanceCsv = (report, periodLabel) => {
    const { period, summary, transactions } = report;
    const lines = [];

    lines.push('Laporan Keuangan Shaff HRIS');
    lines.push(`Periode,${csvEscape(periodLabel)}`);
    lines.push(`Rentang Tanggal,${csvEscape(formatDate(period.start_date))} - ${csvEscape(formatDate(period.end_date))}`);
    lines.push(`Dicetak,${csvEscape(new Date().toLocaleString('id-ID'))}`);
    lines.push('');
    lines.push('RINGKASAN');
    lines.push('Metrik,Nilai');
    lines.push(`Total Pemasukan,${summary.total_income}`);
    lines.push(`Total Pengeluaran,${summary.total_expense}`);
    lines.push(`Saldo Bersih,${summary.net_balance}`);
    lines.push(`Jumlah Transaksi,${summary.transaction_count}`);
    lines.push('');
    lines.push('RINGKASAN PER CHANNEL');
    lines.push('Channel,Pemasukan,Pengeluaran,Saldo');
    summary.by_channel.forEach((row) => {
      lines.push([
        csvEscape(row.channel_name),
        row.income,
        row.expense,
        row.income - row.expense
      ].join(','));
    });
    lines.push('');
    lines.push('RINGKASAN PER KATEGORI');
    lines.push('Tipe,Kategori,Total,Jumlah Transaksi');
    summary.by_category.forEach((row) => {
      lines.push([
        csvEscape(row.type === 'income' ? 'Pemasukan' : 'Pengeluaran'),
        csvEscape(row.category),
        row.total,
        row.count
      ].join(','));
    });
    lines.push('');
    lines.push('DETAIL TRANSAKSI');
    lines.push('Tanggal,Channel,Tipe,Kategori,Jumlah,Deskripsi,Dibuat Oleh,Referensi');
    transactions.forEach((tx) => {
      lines.push([
        csvEscape(formatDate(tx.transaction_date)),
        csvEscape(tx.channel_name),
        csvEscape(tx.type === 'income' ? 'Pemasukan' : 'Pengeluaran'),
        csvEscape(tx.category),
        tx.amount,
        csvEscape(tx.description || ''),
        csvEscape(tx.created_by_username || ''),
        csvEscape(tx.reference_type || '')
      ].join(','));
    });

    return lines.join('\n');
  };

  const handleExportReport = async () => {
    try {
      setExportLoading(true);
      const { start_date, end_date } = getExportDateRange(exportPeriod, exportMonth);
      const periodLabel = `${EXPORT_PERIOD_LABELS[exportPeriod]} (${exportMonth})`;

      const response = await axios.get(`${API_URL}/finance/reports/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          start_date,
          end_date,
          channel_id: exportChannel || undefined,
          type: exportType || undefined
        }
      });

      const csvContent = buildFinanceCsv(response.data, periodLabel);
      const safePeriod = exportPeriod;
      const safeMonth = exportMonth.replace('-', '');
      downloadCsv(csvContent, `laporan-keuangan-${safeMonth}-${safePeriod}.csv`);

      Swal.fire('Berhasil', 'Laporan keuangan berhasil diexport', 'success');
      setShowExportModal(false);
    } catch (error) {
      console.error('Error exporting finance report:', error);
      Swal.fire('Error', error.response?.data?.error || 'Gagal export laporan keuangan', 'error');
    } finally {
      setExportLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="superadmin">
        <div className="text-center py-8">Loading...</div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="superadmin">
      <div className="bg-slate-50/50 min-h-screen p-4 sm:p-6 lg:p-8 rounded-2xl shadow-inner border border-gray-100">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-extrabold text-gray-800 tracking-tight">Finance Management</h1>
            <p className="text-gray-500 mt-1">Kelola dan pantau arus kas dari berbagai channel bisnis.</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowExportModal(true)}
              className="bg-white border border-gray-300 text-gray-700 font-medium px-4 py-2.5 rounded-lg shadow-sm hover:bg-gray-50 transition-all focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500 flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Export Laporan
            </button>
            <button
              onClick={() => setShowModal(true)}
              className="bg-blue-600 text-white font-medium px-5 py-2.5 rounded-lg shadow-md hover:bg-blue-700 hover:shadow-lg transition-all focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex items-center gap-2"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
              </svg>
              Tambah Transaksi
            </button>
          </div>
        </div>

        {/* 4 Scorecards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
          {/* Total Keuangan */}
          <div className="bg-gradient-to-br from-indigo-600 via-purple-600 to-purple-800 border-none text-white p-6 rounded-2xl shadow-lg relative overflow-hidden group hover:scale-105 hover:shadow-2xl transition-all duration-300 transform cursor-pointer">
            <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-30 group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-gray-300 mb-1 uppercase tracking-wider relative z-10">Total Keuangan</h3>
            <p className="text-3xl font-extrabold mb-4 relative z-10">{formatCurrency(summary.total.total_balance)}</p>

            <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm mt-auto relative z-10">
              <div className="flex justify-between items-center text-xs text-gray-200 mb-1.5">
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-green-400"></span>Pemasukan</span>
                <span className="font-medium text-white">{formatCurrency(summary.total.total_income)}</span>
              </div>
              <div className="flex justify-between items-center text-xs text-gray-200 mb-1.5">
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-red-400"></span>Pengeluaran</span>
                <span className="font-medium text-white">{formatCurrency(summary.total.total_expense)}</span>
              </div>
              <div className="flex justify-between items-center text-xs text-gray-200 pt-1.5 mt-1.5 border-t border-white/10">
                <span className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full bg-yellow-400"></span>Gaji</span>
                <span className="font-medium text-white">{formatCurrency(summary.total.total_salary_expense)}</span>
              </div>
            </div>
          </div>

          {/* Shaff Money */}
          <div className="bg-gradient-to-br from-emerald-500 via-teal-500 to-teal-700 border-none text-white p-6 rounded-2xl shadow-lg relative overflow-hidden group hover:scale-105 hover:shadow-2xl transition-all duration-300 transform cursor-pointer">
            <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-30 group-hover:scale-110 group-hover:-rotate-3 transition-all duration-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h3 className="text-sm font-medium text-emerald-100 mb-1 uppercase tracking-wider relative z-10">Shaff Money</h3>
            <p className="text-3xl font-extrabold mb-4 relative z-10">{formatCurrency(summary.total.shaff_money)}</p>

            <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm mt-auto relative z-10">
              <div className="text-xs text-emerald-50 leading-relaxed font-medium">
                Dana bebas yang dapat digunakan untuk operasional dan pengembangan bisnis di luar kebutuhan gaji.
              </div>
            </div>
          </div>

          {/* Untuk Gaji */}
          <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-orange-700 border-none text-white p-6 rounded-2xl shadow-lg relative overflow-hidden group hover:scale-105 hover:shadow-2xl transition-all duration-300 transform cursor-pointer flex flex-col justify-between">
            <div className="absolute top-4 right-4 opacity-10 group-hover:opacity-30 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>

            <div>
              <h3 className="text-sm font-medium text-amber-100 mb-1 uppercase tracking-wider relative z-10 flex justify-between items-center">
                Untuk Gaji
                <div className="flex gap-1">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowHistoryModal(true);
                    }}
                    className="bg-white/20 hover:bg-white/40 text-white rounded-full p-1.5 transition-colors"
                    title="Riwayat Proporsi"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowProporsiModal(true);
                    }}
                    className="bg-white/20 hover:bg-white/40 text-white rounded-full p-1.5 transition-colors"
                    title="Tambah Proporsi"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                    </svg>
                  </button>
                </div>
              </h3>
              <p className="text-3xl font-extrabold mb-4 relative z-10">{formatCurrency(summary.total.available_salary_fund)}</p>
            </div>

            <div className="bg-white/10 rounded-xl p-3 backdrop-blur-sm relative z-10">
              <div className="flex justify-between items-center text-xs text-orange-50 mb-1.5">
                <span className="flex items-center gap-1 font-medium"><span className="w-1.5 h-1.5 rounded-full bg-blue-300"></span>Total Budget Diinput</span>
                <span className="font-bold text-white">{formatCurrency(summary.total.total_salary_fund_allocated)}</span>
              </div>
              <div className="flex justify-between items-center text-xs text-orange-50 pt-1.5 mt-1.5 border-t border-white/10">
                <span className="flex items-center gap-1 font-medium"><span className="w-1.5 h-1.5 rounded-full bg-yellow-300"></span>Budget Tersisa (Maks Input)</span>
                <span className="font-bold text-white">{formatCurrency(summary.total.sisa_budget_gaji)}</span>
              </div>
            </div>
          </div>

          {/* Channels */}
          {summary.channels.map((channel) => {
            let theme = {
              bg: 'bg-white',
              border: 'border-blue-100',
              iconColor: 'text-blue-500/10',
              titleColor: 'text-blue-600',
              valColor: 'text-gray-900',
              dotIn: 'bg-green-500',
              dotOut: 'bg-red-500',
              dotSal: 'bg-orange-500',
              innerBg: 'bg-gray-50'
            };

            if (channel.code === 'RUNTERA') {
              theme.border = 'border-green-100';
              theme.iconColor = 'text-green-500/10';
              theme.titleColor = 'text-green-600';
            }
            if (channel.code === 'KURSUS_CODING') {
              theme.border = 'border-purple-100';
              theme.iconColor = 'text-purple-500/10';
              theme.titleColor = 'text-purple-600';
            }

            return (
              <div key={channel.id} className={`${theme.bg} ${theme.border} border-t-4 p-6 rounded-2xl shadow-md relative overflow-hidden group hover:scale-105 hover:shadow-2xl transition-all duration-300 transform cursor-pointer`}>
                <div className={`absolute top-4 right-4 ${theme.iconColor} group-hover:scale-110 group-hover:-rotate-3 group-hover:opacity-80 transition-all duration-500 opacity-60`}>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <h3 className={`text-sm font-bold ${theme.titleColor} mb-1 uppercase tracking-wider relative z-10`}>{channel.name}</h3>
                <p className={`text-3xl font-extrabold mb-4 relative z-10 ${theme.valColor}`}>{formatCurrency(channel.balance)}</p>

                <div className={`${theme.innerBg} rounded-xl p-3 border border-gray-100 mt-auto relative z-10`}>
                  <div className="flex justify-between items-center text-xs text-gray-500 mb-1.5">
                    <span className="flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${theme.dotIn}`}></span>Pemasukan</span>
                    <span className="font-semibold text-gray-800">{formatCurrency(channel.total_income)}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-gray-500 mb-1.5">
                    <span className="flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${theme.dotOut}`}></span>Pengeluaran</span>
                    <span className="font-semibold text-gray-800">{formatCurrency(channel.total_expense)}</span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-gray-500 pt-1.5 mt-1.5 border-t border-gray-200">
                    <span className="flex items-center gap-1.5"><span className={`w-1.5 h-1.5 rounded-full ${theme.dotSal}`}></span>Gaji</span>
                    <span className="font-semibold text-gray-800">{formatCurrency(channel.salary_expense)}</span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 mb-6">
          <div className="flex flex-col md:flex-row gap-5">
            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Filter Channel</label>
              <div className="relative">
                <select
                  value={filterChannel}
                  onChange={(e) => setFilterChannel(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50/50 hover:bg-white transition-colors cursor-pointer text-gray-700 font-medium"
                >
                  <option value="">Semua Channel Tersedia</option>
                  {channels.map((channel) => (
                    <option key={channel.id} value={channel.id}>
                      {channel.name}
                    </option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                </div>
              </div>
            </div>

            <div className="flex-1">
              <label className="block text-sm font-semibold text-gray-700 mb-1.5">Kategori Tipe</label>
              <div className="relative">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-4 py-2.5 appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50/50 hover:bg-white transition-colors cursor-pointer text-gray-700 font-medium"
                >
                  <option value="">Semua Tipe Transaksi</option>
                  <option value="income">Pemasukan (Income)</option>
                  <option value="expense">Pengeluaran (Expense)</option>
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none text-gray-500">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Transactions Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-5 border-b border-gray-200 bg-white">
            <h2 className="text-lg font-bold text-gray-800">Riwayat Transaksi</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50/50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Tanggal</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Channel</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Kategori</th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-500 uppercase tracking-wider">Deskripsi</th>
                  <th className="px-6 py-4 text-right text-xs font-bold text-gray-500 uppercase tracking-wider">Jumlah</th>
                  <th className="px-6 py-4 text-center text-xs font-bold text-gray-500 uppercase tracking-wider">Aksi</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {transactions.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center">
                      <div className="flex flex-col items-center justify-center text-gray-400">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mb-3 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        <p className="text-lg font-medium text-gray-500">Belum ada transaksi</p>
                        <p className="text-sm mt-1">Transaksi yang anda tambahkan akan muncul di sini.</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  transactions.map((transaction) => (
                    <tr key={transaction.id} className="hover:bg-blue-50/30 transition-colors group">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-gray-100 flex items-center justify-center text-gray-500 mr-3">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                          <span className="text-sm font-medium text-gray-900">{formatDate(transaction.transaction_date)}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm font-semibold text-gray-800">{transaction.channel_name}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${transaction.type === 'income'
                          ? 'bg-green-50 text-green-700 border-green-200'
                          : 'bg-red-50 text-red-700 border-red-200'
                          }`}>
                          {transaction.type === 'income' ? (
                            <><svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 11l5-5m0 0l5 5m-5-5v12"></path></svg> Pemasukan</>
                          ) : (
                            <><svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 13l-5 5m0 0l-5-5m5 5V6"></path></svg> Pengeluaran</>
                          )}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{transaction.category}</div>
                        {transaction.reference_type === 'salary' && (
                          <span className="inline-block mt-1 text-xs bg-indigo-50 text-indigo-700 border border-indigo-100 px-2 py-0.5 rounded-md font-medium">
                            Gaji
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate" title={transaction.description}>
                        {transaction.description || <span className="text-gray-400 italic">Tidak ada deskripsi</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <span className={`text-sm font-bold ${transaction.type === 'income' ? 'text-green-600' : 'text-gray-900'}`}>
                          {transaction.type === 'income' ? '+' : '-'} {formatCurrency(transaction.amount)}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-center">
                        {transaction.reference_type !== 'salary' ? (
                          <button
                            onClick={() => handleDelete(transaction.id)}
                            className="bg-red-50 text-red-600 hover:bg-red-100 hover:text-red-800 px-3 py-1.5 rounded-lg text-xs font-semibold border border-red-200 transition-colors"
                          >
                            Hapus
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal Form */}
        {showModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4">Tambah Transaksi</h2>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Channel *</label>
                  <select
                    value={formData.channel_id}
                    onChange={(e) => setFormData({ ...formData, channel_id: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    required
                  >
                    <option value="">Pilih Channel</option>
                    {channels.map((channel) => (
                      <option key={channel.id} value={channel.id}>
                        {channel.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Tipe *</label>
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    required
                  >
                    <option value="income">Pemasukan</option>
                    <option value="expense">Pengeluaran</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Kategori *</label>
                  <input
                    type="text"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="Contoh: Sales, Operational, Marketing"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Jumlah (IDR) *</label>
                  <input
                    type="text"
                    value={formatInputCurrency(formData.amount)}
                    onChange={(e) => setFormData({ ...formData, amount: unformatCurrency(e.target.value) })}
                    className="w-full border rounded px-3 py-2"
                    placeholder="0"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Tanggal *</label>
                  <input
                    type="date"
                    value={formData.transaction_date}
                    onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Deskripsi</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                    rows="3"
                    placeholder="Keterangan tambahan (opsional)"
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                  >
                    Simpan
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400"
                  >
                    Batal
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Proporsi Modal Form */}
        {showProporsiModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4">Tambah Proporsi Gaji</h2>

              <form onSubmit={handleProporsiSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Sumber Channel *</label>
                  <select
                    value={proporsiChannel}
                    onChange={(e) => setProporsiChannel(e.target.value)}
                    className="w-full border rounded px-3 py-2"
                    required
                  >
                    <option value="">Pilih Channel</option>
                    {channels.map((channel) => (
                      <option key={channel.id} value={channel.id}>
                        {channel.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Jumlah (IDR) *</label>
                  <input
                    type="text"
                    value={formatInputCurrency(proporsiAmount)}
                    onChange={(e) => setProporsiAmount(unformatCurrency(e.target.value))}
                    className="w-full border rounded px-3 py-2"
                    placeholder="0"
                    required
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <button
                    type="submit"
                    className="flex-1 bg-amber-500 text-white px-4 py-2 rounded hover:bg-amber-600 font-medium"
                  >
                    Simpan Proporsi
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setShowProporsiModal(false);
                      setProporsiAmount('');
                    }}
                    className="flex-1 bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 font-medium"
                  >
                    Batal
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Export Report Modal */}
        {showExportModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl p-6 w-full max-w-lg shadow-xl">
              <h2 className="text-xl font-bold mb-1">Export Laporan Keuangan</h2>
              <p className="text-sm text-gray-500 mb-5">
                Tarik data pemasukan, pengeluaran, dan ringkasan laporan ke file CSV.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Periode Laporan *</label>
                  <select
                    value={exportPeriod}
                    onChange={(e) => setExportPeriod(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2.5"
                  >
                    <option value="monthly">Bulanan</option>
                    <option value="quarter">Per 3 Bulan</option>
                    <option value="semester">Per 6 Bulan</option>
                    <option value="yearly">Per Tahun</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    {exportPeriod === 'monthly' && 'Export 1 bulan berdasarkan bulan referensi.'}
                    {exportPeriod === 'quarter' && 'Export 3 bulan terakhir hingga bulan referensi.'}
                    {exportPeriod === 'semester' && 'Export 6 bulan terakhir hingga bulan referensi.'}
                    {exportPeriod === 'yearly' && 'Export seluruh tahun dari tahun referensi.'}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    {exportPeriod === 'yearly' ? 'Tahun Referensi *' : 'Bulan Referensi *'}
                  </label>
                  <input
                    type="month"
                    value={exportMonth}
                    onChange={(e) => setExportMonth(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2.5"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Filter Channel</label>
                  <select
                    value={exportChannel}
                    onChange={(e) => setExportChannel(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2.5"
                  >
                    <option value="">Semua Channel</option>
                    {channels.map((channel) => (
                      <option key={channel.id} value={channel.id}>
                        {channel.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Filter Tipe</label>
                  <select
                    value={exportType}
                    onChange={(e) => setExportType(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2.5"
                  >
                    <option value="">Semua Tipe</option>
                    <option value="income">Pemasukan Saja</option>
                    <option value="expense">Pengeluaran Saja</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-2 pt-6">
                <button
                  type="button"
                  onClick={handleExportReport}
                  disabled={exportLoading}
                  className="flex-1 bg-emerald-600 text-white px-4 py-2.5 rounded-lg hover:bg-emerald-700 font-medium disabled:opacity-60"
                >
                  {exportLoading ? 'Mengekspor...' : 'Download CSV'}
                </button>
                <button
                  type="button"
                  onClick={() => setShowExportModal(false)}
                  className="flex-1 bg-gray-200 text-gray-700 px-4 py-2.5 rounded-lg hover:bg-gray-300 font-medium"
                >
                  Batal
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Salary Fund History Modal */}
        <SalaryFundHistoryModal
          isOpen={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          onRefresh={fetchData}
        />

        {/* Salary Breakdown Modal */}
        <SalaryBreakdownModal
          isOpen={showBreakdownModal}
          onClose={() => setShowBreakdownModal(false)}
        />
      </div>
    </DashboardLayout>
  );
};

export default Finance;
